import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaGlobe, FaCoins, FaCaretDown, FaChartBar, FaPencilAlt, FaSignOutAlt, FaUser, FaQuestionCircle, FaMagic, FaImages } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import RetroWordmark from './RetroWordmark';
import { FOUNDER_EMAILS } from '../config';

const Navbar = ({ tokenBalance = 0, onWordmarkClick, wordmarkRef, hasDrawings = false }) => {
  const location = useLocation();
  const { user, logout } = useAuth0();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isFounder = user?.email && FOUNDER_EMAILS.includes(user.email);

  // Close mobile menu when route changes
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const mainLinks = [
    { path: '/create', icon: <FaHeart />, label: 'Create' },
    { path: '/decks', icon: <FaImages />, label: 'Decks' },
    // { path: '/prompt-craft', icon: <FaMagic />, label: 'PromptCraft' },
    { path: '/public', icon: <FaGlobe />, label: 'Public' },
    { path: '/gallery', icon: <FaImages />, label: 'Gallery' },
    ...(hasDrawings ? [{ path: '/drawings', icon: <FaPencilAlt />, label: 'Drawings' }] : []),
    { path: '/usage', icon: <FaChartBar />, label: 'Usage' },
    { path: '/tokens', icon: <FaCoins />, label: 'Tokens' },
  ];

  const adminLinks = [
    { path: '/admin', icon: <FaChartBar />, label: 'Admin Dashboard' },
  ];

  return (
    <header className="h-24 flex justify-between items-center px-4 md:px-8 bg-[#F8F7F4] sticky top-0 z-40">
      <div className="flex-1 flex justify-center md:justify-start" onClick={onWordmarkClick}>
        <RetroWordmark ref={wordmarkRef} size="md" alignment="left" />
      </div>
      <div className="flex items-center gap-4 md:gap-8">
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4 md:gap-8">
          {mainLinks.map(({ path, icon, label }) => (
            <Link key={path} to={path}>
              <motion.div 
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.span 
                  className={`font-['VT323'] text-[16px] md:text-[18px] transition-all duration-200
                    ${location.pathname === path ? getActiveIconColor(path) : 'text-black/20 hover:text-black/40'}`}
                >
                  <div className="flex items-center gap-1 md:gap-2">
                    {React.cloneElement(icon, { 
                      className: location.pathname === path ? getActiveIconColor(path) : 'text-black/20'
                    })}
                    <span>{label}</span>
                  </div>
                </motion.span>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* Mobile Hamburger Menu - INTENTIONAL DESIGN: Uses 🍔 emoji, not FaBars icon */}
        <div className="md:hidden">
          <motion.button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="flex items-center justify-center w-8 h-8 text-black/40 hover:text-black/60 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* DO NOT REPLACE: 🍔 and ❌ emojis are intentional brand choice */}
            {showMobileMenu ? <span className="text-xl">❌</span> : <span className="text-xl">🍔</span>}
          </motion.button>
        </div>

        {/* Desktop User Menu */}
        <div className="hidden md:block relative">
          <motion.button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="w-7 h-7 rounded-full bg-black/[0.08] overflow-hidden">
              <img 
                src={user?.picture} 
                alt={user?.name || 'User avatar'} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="flex items-center justify-center w-full h-full text-sm font-medium text-black/70">${user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}</span>`;
                }}
              />
            </div>
            <FaCaretDown className={`transition-transform duration-200 text-black/20 hover:text-black/40 ${showDropdown ? 'rotate-180 text-black/40' : ''}`} />
          </motion.button>

          <AnimatePresence>
            {showDropdown && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-2 w-48 py-2 bg-white/90 backdrop-blur-xl rounded-lg shadow-lg border border-black/[0.08]"
              >
                <div className="px-4 py-2 border-b border-black/[0.08]">
                  <div className="font-['VT323'] text-[16px] text-black/40">{user?.name || user?.email}</div>
                </div>

                {/* Admin Links */}
                {isFounder && (
                  <>
                    {adminLinks.map(({ path, icon, label }) => (
                      <Link
                        key={path}
                        to={path}
                        onClick={() => setShowDropdown(false)}
                        className={`w-full flex items-center gap-2 px-4 py-2 font-['VT323'] text-[16px] ${
                          location.pathname === path 
                            ? 'text-black/60 bg-black/[0.03]' 
                            : 'text-black/20 hover:text-black/40 hover:bg-black/[0.03]'
                        } transition-colors`}
                      >
                        {React.cloneElement(icon, { 
                          className: `w-4 h-4 ${location.pathname === path ? getActiveIconColor(path) : ''}`
                        })}
                        {label}
                      </Link>
                    ))}
                    <div className="border-t border-black/[0.08] my-2"></div>
                  </>
                )}

                {/* Support Links */}
                <Link
                  to="/support"
                  onClick={() => setShowDropdown(false)}
                  className={`w-full flex items-center gap-2 px-4 py-2 font-['VT323'] text-[16px] ${
                    location.pathname === '/support' 
                      ? 'text-black/60 bg-black/[0.03]' 
                      : 'text-black/20 hover:text-black/40 hover:bg-black/[0.03]'
                  } transition-colors`}
                >
                  <FaQuestionCircle className="w-4 h-4" />
                  Support
                </Link>

                <div className="border-t border-black/[0.08] my-2"></div>

                <button
                  onClick={async () => {
                    try {
                      console.log('Logging out...');
                      setShowDropdown(false);
                      
                      await logout({
                        logoutParams: {
                          returnTo: window.location.origin,
                          client_id: process.env.REACT_APP_AUTH0_CLIENT_ID
                        }
                      });
                    } catch (error) {
                      console.error('Logout error:', error);
                      if (error.message?.includes('rate limit')) {
                        window.location.href = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/v2/logout?client_id=${process.env.REACT_APP_AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(window.location.origin)}`;
                      }
                    }
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 font-['VT323'] text-[16px] text-black/20 hover:text-black/40 hover:bg-black/[0.03] transition-colors"
                >
                  Sign Out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed top-24 left-0 right-0 z-50 bg-[#F8F7F4] border-b border-black/[0.08] shadow-lg"
          >
            <div className="px-4 py-6 max-h-[calc(100vh-6rem)] overflow-y-auto">
              {/* User Info */}
              <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-black/[0.03] rounded-lg">
                <div className="w-8 h-8 rounded-full bg-black/[0.08] overflow-hidden">
                  <img 
                    src={user?.picture} 
                    alt={user?.name || 'User avatar'} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="flex items-center justify-center w-full h-full text-sm font-medium text-black/70">${user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}</span>`;
                    }}
                  />
                </div>
                <div className="font-['VT323'] text-[16px] text-black/60">{user?.name || user?.email}</div>
              </div>

              {/* Main Navigation Items */}
              <div className="space-y-2 mb-4">
                {mainLinks.map(({ path, icon, label }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-['VT323'] text-[18px] transition-colors ${
                      location.pathname === path 
                        ? `${getActiveIconColor(path)} bg-black/[0.03]` 
                        : 'text-black/40 hover:text-black/60 hover:bg-black/[0.03]'
                    }`}
                  >
                    {React.cloneElement(icon, { 
                      className: `w-5 h-5 ${location.pathname === path ? getActiveIconColor(path) : ''}`
                    })}
                    {label}
                  </Link>
                ))}
              </div>

              {/* Admin Links */}
              {isFounder && (
                <div className="space-y-2 mb-4 pt-4 border-t border-black/[0.08]">
                  <div className="px-4 py-2 font-['VT323'] text-[14px] text-black/40 uppercase tracking-wide">
                    Admin
                  </div>
                  {adminLinks.map(({ path, icon, label }) => (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setShowMobileMenu(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-['VT323'] text-[18px] transition-colors ${
                        location.pathname === path 
                          ? `${getActiveIconColor(path)} bg-black/[0.03]` 
                          : 'text-black/40 hover:text-black/60 hover:bg-black/[0.03]'
                      }`}
                    >
                      {React.cloneElement(icon, { 
                        className: `w-5 h-5 ${location.pathname === path ? getActiveIconColor(path) : ''}`
                      })}
                      {label}
                    </Link>
                  ))}
                </div>
              )}

              {/* Support Links */}
              <div className="space-y-2 mb-4 pt-4 border-t border-black/[0.08]">
                <Link
                  to="/support"
                  onClick={() => setShowMobileMenu(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-['VT323'] text-[18px] transition-colors ${
                    location.pathname === '/support' 
                      ? 'text-black/60 bg-black/[0.03]' 
                      : 'text-black/40 hover:text-black/60 hover:bg-black/[0.03]'
                  }`}
                >
                  <FaQuestionCircle className="w-5 h-5" />
                  Support
                </Link>
              </div>

              {/* Logout */}
              <div className="pt-4 border-t border-black/[0.08]">
                <button
                  onClick={async () => {
                    try {
                      console.log('Logging out...');
                      setShowMobileMenu(false);
                      
                      await logout({
                        logoutParams: {
                          returnTo: window.location.origin,
                          client_id: process.env.REACT_APP_AUTH0_CLIENT_ID
                        }
                      });
                    } catch (error) {
                      console.error('Logout error:', error);
                      if (error.message?.includes('rate limit')) {
                        window.location.href = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/v2/logout?client_id=${process.env.REACT_APP_AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(window.location.origin)}`;
                      }
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-['VT323'] text-[18px] text-black/40 hover:text-black/60 hover:bg-black/[0.03] transition-colors"
                >
                  <FaSignOutAlt className="w-5 h-5" />
                  Sign Out
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const getActiveIconColor = (path) => {
  switch (path) {
    case '/create': return 'text-red-500';
    case '/public': return 'text-green-400';
    case '/drawings': return 'text-pink-500';
    case '/usage': return 'text-blue-500';
    case '/tokens': return 'text-yellow-500';
    case '/admin': return 'text-gray-500';
    default: return 'text-black/20';
  }
};

export default Navbar;